/**
 * Fichar. La pantalla que se ve el 95% del tiempo.
 *
 * Se usa de pie, con prisa, con guantes y a contraluz: un estado legible a un
 * metro, un botón enorme y una sola decisión.
 *
 * El flujo de entrada pasa por el QR del centro cuando el tenant tiene centros
 * dados de alta (obra, local, nave). La salida y las pausas no lo piden: ya
 * hay jornada abierta y el centro está en el ledger. Una oficina sin centros
 * ficha directo, sin cámara.
 *
 * La geovalla se comprueba ANTES de pulsar y se muestra siempre: el trabajador
 * ve que está dentro sin tener que fallar primero. Es INFORMATIVA: nunca
 * bloquea el fichaje — decide el backend (VALID o PENDING_APPROVAL con su
 * revisión), que es quien ve la precisión del fix y la geovalla fresca.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePunch } from '../hooks/usePunch';
import { useAppStore } from '../store/useAppStore';
import { BreakReason, WorkMode } from '../db/database';
import { Worksite } from '../services/apiClient';
import { cachedWorksites, checkGeofence, refreshWorksites } from '../services/worksites';
import { QrScanner } from './QrScanner';
import { Icon, IconName } from './ui/Icon';

interface PauseOption {
  value: BreakReason;
  name: string;
  icon: IconName;
  paid: boolean;
}

const PAUSES: PauseOption[] = [
  // ⚠️ Estas etiquetas tienen que decir lo MISMO que
  // `PAID_BREAK_REASONS` del backend ({COFFEE, SMOKE, BATHROOM}), que es quien
  // calcula el día. Discrepaban en tres: "Cigarro" salía como no pagada
  // cuando sí lo es, y "Gestión" y "Recado" salían con borde verde y la
  // etiqueta "Pagada" cuando el motor las descuenta. El trabajador pulsaba
  // "Recado", veía que le contaba como trabajado, y no le contaba: una
  // reclamación de nómina con la captura de la app en la mano.
  { value: 'COFFEE', name: 'Café', icon: 'coffee', paid: true },
  { value: 'SMOKE', name: 'Cigarro', icon: 'cigarette', paid: true },
  { value: 'BATHROOM', name: 'Baño', icon: 'toilet', paid: true },
  { value: 'LUNCH', name: 'Comida', icon: 'utensils', paid: false },
  { value: 'PERSONAL_CALL', name: 'Llamada personal', icon: 'phone', paid: false },
  { value: 'ADMIN_CALL', name: 'Gestión', icon: 'clipboard', paid: false },
  { value: 'ERRAND', name: 'Recado', icon: 'errand', paid: false },
  { value: 'OTHER', name: 'Otros', icon: 'dots', paid: false },
];

type Position = { latitude?: number; longitude?: number; accuracy?: number } | null;

export function PunchScreen() {
  const { workState, syncStatus, user } = useAppStore();
  const { isLoading, punchIn, punchOut, startBreak, endBreak, getCurrentLocation } = usePunch();

  const [worksites, setWorksites] = useState<Worksite[]>(cachedWorksites);
  const [position, setPosition] = useState<Position>(null);
  const [scanning, setScanning] = useState(false);
  const [showPauses, setShowPauses] = useState(false);
  // Guarda de concurrencia (ronda 4, r2b18[2]): cubre desde el PRIMER toque
  // hasta que el evento está en la cola. `isLoading` del hook no se activa
  // hasta `handlePunch`, que corre DESPUÉS del await de GPS (hasta 10 s con
  // un fix lento bajo techo): en esa ventana el botón seguía vivo y un doble
  // toque fabricaba dos marcas idénticas. El ref corta en el acto (los
  // setState de React no son inmediatos); el estado deshabilita el botón.
  const enCursoRef = useRef(false);
  const [enCurso, setEnCurso] = useState(false);
  // Dónde se trabaja la jornada que se va a abrir. Solo se elige al ENTRAR:
  // la salida y las pausas ya pasan dentro de una jornada que sabe su modalidad.
  const [workMode, setWorkMode] = useState<WorkMode>('ONSITE');

  const { isClockedIn, isOnBreak, breakReason, lastEventTime } = workState;
  // La modalidad de la jornada ABIERTA (la declaró su entrada). La salida y
  // las pausas de una jornada REMOTE no adquieren ni transmiten GPS: la
  // pantalla prometió "no se te pide la ubicación" y antes sólo la entrada
  // lo cumplía (ronda 4, r2b18[1]).
  const jornadaRemota = workState.workMode === 'REMOTE';

  // Centros y posición: los queremos ANTES de que pulse, no después.
  useEffect(() => {
    refreshWorksites().then(setWorksites).catch(() => {
      // Sin red nos quedamos con la caché. Es exactamente el caso de la obra.
    });
  }, []);

  useEffect(() => {
    let alive = true;
    getCurrentLocation().then((p) => {
      if (alive) setPosition(p);
    });
    const id = setInterval(() => {
      getCurrentLocation().then((p) => {
        if (alive) setPosition(p);
      });
    }, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [getCurrentLocation]);

  /** El centro más cercano donde SÍ se puede fichar, o el más cercano a secas. */
  const nearest = (() => {
    const geofenced = worksites.filter(
      (w) => w.require_geofence && w.geo_latitude != null && w.geo_longitude != null
    );
    if (!geofenced.length || !position?.latitude) return null;

    return geofenced
      .map((site) => ({ site, check: checkGeofence(site, position) }))
      .sort((a, b) => (a.check.distance ?? Infinity) - (b.check.distance ?? Infinity))[0];
  })();

  const triggerHaptic = () => {
    if ('vibrate' in navigator) navigator.vibrate(50);
  };

  /** Entrada con el centro ya escaneado. */
  const doPunchIn = useCallback(
    async (worksiteUuid?: string, qrToken?: string, mode: WorkMode = 'ONSITE') => {
      // A distancia no se pide ubicación: acreditamos el tiempo, no dónde vive
      // la persona. Ni geovalla, ni centro, ni GPS.
      if (mode === 'REMOTE') {
        await punchIn(undefined, undefined, undefined, 'REMOTE');
        return;
      }

      const loc = (await getCurrentLocation()) ?? undefined;

      // La geovalla local NUNCA bloquea (ronda 4, r2b10[1]): esta caché puede
      // estar rancia (pin movido sin red para refrescar) y no mira la
      // precisión del fix, mientras que el backend tiene la regla tolerante
      // con GPS malo (fuera de radio con precisión dudosa → a revisión, no
      // violación). Bloquear aquí dejaba la jornada SIN registrar hasta una
      // corrección manual, y el único escape era declararse "En casa" —
      // falsificando el work_mode del ledger. Se encola igual; decide el
      // servidor (VALID o PENDING_APPROVAL) y el aviso visual de arriba
      // sigue diciendo a cuántos metros está el centro.
      await punchIn(loc, worksiteUuid, qrToken);
    },
    [getCurrentLocation, punchIn]
  );

  const handleMain = async () => {
    if (enCursoRef.current) return;
    enCursoRef.current = true;
    setEnCurso(true);
    triggerHaptic();

    try {
      if (isOnBreak) {
        // Jornada REMOTE: sin GPS ni en la pausa ni en la salida.
        await endBreak(jornadaRemota ? undefined : (await getCurrentLocation()) ?? undefined);
        return;
      }
      if (isClockedIn) {
        await punchOut(jornadaRemota ? undefined : (await getCurrentLocation()) ?? undefined);
        return;
      }
      // Entrada a distancia: ni QR ni centro, se ficha y ya.
      if (workMode === 'REMOTE') {
        await doPunchIn(undefined, undefined, 'REMOTE');
        return;
      }
      // Entrada: si la empresa tiene centros, el QR dice en cuál.
      if (worksites.length > 0) {
        setScanning(true);
        return;
      }
      await doPunchIn();
    } finally {
      enCursoRef.current = false;
      setEnCurso(false);
    }
  };

  if (scanning) {
    return (
      <QrScanner
        onCancel={() => setScanning(false)}
        onScan={async (uuid, qrToken) => {
          // No se reabre la pantalla de fichar hasta terminar el doPunchIn
          // (ronda 4, r2b18[2]): volvía con el botón grande vivo mientras la
          // entrada aún se tramitaba y el segundo toque duplicaba la marca.
          // El escáner ya paró la cámara solo (doneRef), así que aquí sólo
          // queda esperar.
          try {
            await doPunchIn(uuid, qrToken);
          } finally {
            setScanning(false);
          }
        }}
      />
    );
  }

  const status = isOnBreak
    ? { label: 'EN PAUSA', color: 'text-warn' }
    : isClockedIn
      ? { label: 'TRABAJANDO', color: 'text-brand' }
      : { label: 'FUERA', color: 'text-ink-3' };

  const main = isOnBreak
    ? { label: 'FIN PAUSA', icon: 'play' as IconName, bg: 'var(--brand)', fg: 'var(--on-brand)' }
    : isClockedIn
      ? { label: 'SALIDA', icon: 'stop' as IconName, bg: 'var(--danger)', fg: 'var(--on-danger)' }
      : { label: 'ENTRADA', icon: 'play' as IconName, bg: 'var(--brand)', fg: 'var(--on-brand)' };

  const lastLabel = lastEventTime
    ? `Último fichaje: ${new Date(lastEventTime).toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : 'Sin fichajes todavía';

  return (
    <div className="flex-1 flex flex-col">
      {/* Saludo y conexión */}
      <div className="px-4 pt-4 flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-2.5">
          <span className="font-display font-extrabold text-[18px]">
            {user?.firstName ? `Hola, ${user.firstName}` : 'Kakuat'}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 font-mono text-[11.5px] font-semibold border ${
              syncStatus.isOnline
                ? 'text-brand bg-brand-soft border-line-strong'
                : 'text-info bg-info-soft border-info'
            }`}
          >
            <Icon name={syncStatus.isOnline ? 'wifi' : 'wifi-off'} size={13} strokeWidth={2} />
            {syncStatus.isOnline ? 'En línea' : 'Sin conexión'}
          </span>
        </div>

        {/* Dónde trabajas hoy. Solo al entrar, y solo si hay algo que elegir. */}
        {!isClockedIn && !isOnBreak && (
          <div className="grid grid-cols-2 gap-2" role="group" aria-label="Dónde trabajas">
            {(
              [
                { mode: 'ONSITE' as WorkMode, label: 'En el centro', icon: 'pin' as IconName },
                { mode: 'REMOTE' as WorkMode, label: 'En casa', icon: 'home' as IconName },
              ]
            ).map((opt) => {
              const on = workMode === opt.mode;
              return (
                <button
                  key={opt.mode}
                  type="button"
                  onClick={() => setWorkMode(opt.mode)}
                  aria-pressed={on}
                  className={`flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 border font-semibold text-[14.5px] transition-colors ${
                    on
                      ? 'bg-brand-soft border-line-strong text-brand'
                      : 'bg-surface border-line text-ink-2'
                  }`}
                >
                  <Icon name={opt.icon} size={18} strokeWidth={1.8} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {workMode === 'REMOTE' && !isClockedIn && !isOnBreak && (
          <p className="text-[13px] text-ink-2 px-1">
            Trabajando a distancia no se te pide la ubicación: se registra tu tiempo, no
            dónde estás.
          </p>
        )}

        {/* Geovalla: se ve antes de pulsar, no después de fallar */}
        {workMode === 'ONSITE' && nearest && (
          <div
            className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 border ${
              nearest.check.inside
                ? 'bg-brand-soft border-line-strong'
                : 'bg-warn-soft border-warn'
            }`}
          >
            <span className="w-9 h-9 rounded-xl bg-canvas grid place-items-center shrink-0">
              <Icon
                name={nearest.check.inside ? 'pin' : 'pin-off'}
                size={20}
                className={nearest.check.inside ? 'text-brand' : 'text-warn'}
                strokeWidth={1.8}
              />
            </span>
            <span className="flex-1 min-w-0 text-left">
              <span
                className={`block font-bold text-[15.5px] ${
                  nearest.check.inside ? 'text-brand' : 'text-warn'
                }`}
              >
                {nearest.check.inside ? `Dentro de ${nearest.site.name}` : `Lejos de ${nearest.site.name}`}
              </span>
              <span className="block text-[13.5px] text-ink-2">
                {/* Fuera de radio NO se bloquea (decide el backend): un GPS
                    que lee 250 m con ±120 m en una calle estrecha puede tener
                    al operario DENTRO, y la caché local puede estar rancia.
                    Se encola la marca y la revisión decide. */}
                {nearest.check.inside
                  ? 'Puedes fichar'
                  : 'Puedes fichar igualmente: quedará a revisión de tu responsable'}
              </span>
            </span>
            {nearest.check.distance != null && (
              <span
                className={`font-mono text-[12px] font-semibold shrink-0 ${
                  nearest.check.inside ? 'text-brand' : 'text-warn'
                }`}
              >
                {nearest.check.distance} m
              </span>
            )}
          </div>
        )}
      </div>

      {/* Offline: tranquilizador, no alarmante */}
      {!syncStatus.isOnline && (
        <div className="mx-4 mt-3 flex items-center gap-3 rounded-2xl bg-info-soft border border-info px-4 py-3">
          <Icon name="wifi-off" size={22} className="text-info shrink-0" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-semibold text-[14.5px] text-info">Sin cobertura. Puedes fichar igual.</p>
            <p className="text-[13px] text-ink-2">Se subirá solo al recuperar señal.</p>
          </div>
          {syncStatus.pendingCount > 0 && (
            <div className="text-center shrink-0">
              <p className="font-display font-extrabold text-[22px] text-info">{syncStatus.pendingCount}</p>
              <p className="font-mono text-[9.5px] uppercase text-ink-2">en cola</p>
            </div>
          )}
        </div>
      )}

      {/* Estado + botón */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-1.5">
        <p className="font-mono text-[12.5px] uppercase tracking-[0.16em] text-ink-3">Estado actual</p>
        <p className={`font-display font-black text-[clamp(40px,13vw,52px)] leading-none ${status.color}`}>
          {status.label}
        </p>
        <p className="font-mono text-[13px] text-ink-2 mb-3.5">
          {isOnBreak && breakReason
            ? `En pausa: ${PAUSES.find((p) => p.value === breakReason)?.name ?? breakReason}`
            : lastLabel}
        </p>

        <button
          onClick={handleMain}
          disabled={isLoading || enCurso}
          className="w-[230px] h-[230px] rounded-full flex flex-col items-center justify-center gap-2.5 disabled:opacity-50 animate-halo"
          style={{ background: main.bg, color: main.fg }}
        >
          <Icon name={main.icon} size={52} strokeWidth={1.5} />
          <span className="font-display font-extrabold text-[26px]">
            {isLoading || enCurso ? 'Procesando…' : main.label}
          </span>
        </button>
      </div>

      {/* Pausa */}
      {isClockedIn && (
        <div className="px-6 pb-5">
          <button
            onClick={() => {
              triggerHaptic();
              if (isOnBreak) {
                handleMain();
              } else {
                setShowPauses(true);
              }
            }}
            disabled={isLoading || enCurso}
            className="w-full h-[60px] rounded-2xl border border-line-strong bg-surface font-bold text-[17px] flex items-center justify-center gap-2.5 disabled:opacity-40"
          >
            <Icon name={isOnBreak ? 'play' : 'pause'} size={22} strokeWidth={1.8} />
            {isOnBreak ? 'Reanudar' : 'Pausa'}
          </button>
        </div>
      )}

      {/* Motivo de pausa */}
      {showPauses && (
        <div
          className="fixed inset-0 z-40 bg-black/50 flex items-end"
          onClick={() => setShowPauses(false)}
        >
          <div
            className="w-full bg-canvas rounded-t-3xl px-4 pt-4 pb-6 animate-rise safe-area-bottom"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-11 h-1.5 rounded-full bg-line-strong mx-auto mb-4" />
            <h2 className="font-display font-extrabold text-[22px] mb-1">Motivo de la pausa</h2>
            <p className="text-ink-2 text-[14px] mb-4">
              El borde verde cuenta como tiempo trabajado; el gris, no.
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              {PAUSES.map((p) => (
                <button
                  key={p.value}
                  disabled={enCurso}
                  onClick={async () => {
                    if (enCursoRef.current) return;
                    enCursoRef.current = true;
                    setEnCurso(true);
                    setShowPauses(false);
                    triggerHaptic();
                    try {
                      // Pausa de una jornada REMOTE: sin GPS. La pantalla
                      // prometió que a distancia no se pide la ubicación y
                      // aquí también se pedía (ronda 4, r2b18[1]).
                      await startBreak(
                        p.value,
                        jornadaRemota ? undefined : (await getCurrentLocation()) ?? undefined
                      );
                    } finally {
                      enCursoRef.current = false;
                      setEnCurso(false);
                    }
                  }}
                  className="flex items-center gap-3 rounded-2xl bg-surface border border-line px-3 py-3.5 text-left"
                  style={{ borderLeft: `5px solid ${p.paid ? 'var(--brand)' : 'var(--ink-3)'}` }}
                >
                  <Icon name={p.icon} size={22} className="text-ink-2 shrink-0" />
                  <span className="min-w-0">
                    <span className="block font-semibold text-[15px] truncate">{p.name}</span>
                    <span
                      className="block font-mono text-[10.5px] uppercase tracking-wider"
                      style={{ color: p.paid ? 'var(--brand)' : 'var(--ink-3)' }}
                    >
                      {p.paid ? 'Pagada' : 'No pagada'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
